import React, { useEffect, useState } from 'react';
import { FiHome, FiLayers, FiBook, FiFileText, FiPlus, FiEdit, FiTrash2 } from 'react-icons/fi';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Modal from '../../components/common/Modal';
import Input from '../../components/common/Input';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { showToast } from '../../components/common/Toaster';
import { publisherGradeAPI } from '../../api/publisherEndpoints';
import {
  DashboardContainer,
  DashboardHeader,
  SectionTitle,
  GradeGrid,
  GradeCard,
  GradeCardHeader,
  GradeCardBody,
  GradeCardFooter,
  GradeActions,
  ActionButton,
  EmptyState,
  FilterBar,
  SearchInput,
  FilterSelect,
} from '../dashboards/Dashboard.styles';

const PublisherGrades = () => {
  const [loading, setLoading] = useState(true);
  const [grades, setGrades] = useState([]);
  const [pagination, setPagination] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [editingGrade, setEditingGrade] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    is_active: true,
  });

  const [filters, setFilters] = useState({
    search: '',
    is_active: '',
    sort_by: 'created_at',
    sort_order: 'desc',
    page: 1,
    per_page: 20,
  });

  const menuItems = [
    { icon: <FiHome />, label: 'Dashboard', path: '/publisher' },
    { icon: <FiFileText />, label: 'Publications', path: '/publisher/publications' },
    { icon: <FiLayers />, label: 'Grades', path: '/publisher/grades' },
    { icon: <FiBook />, label: 'Subjects', path: '/publisher/subjects' },
  ];

  useEffect(() => {
    fetchGrades();
  }, [filters]);

  const fetchGrades = async () => {
    try {
      setLoading(true);
      const response = await publisherGradeAPI.getGrades(filters);
      setGrades(response.data?.grades || []);
      setPagination(response.data?.pagination || {});
    } catch (error) {
      showToast('error', 'Failed to load grades');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({ name: '', is_active: true });
    setEditingGrade(null);
    setShowModal(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await publisherGradeAPI.createGrade({ name: formData.name });
      showToast('success', 'Grade created successfully');
      resetForm();
      fetchGrades();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to create grade');
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingGrade) return;
    setSubmitting(true);

    try {
      await publisherGradeAPI.updateGrade(editingGrade.id, {
        name: formData.name,
        is_active: formData.is_active,
      });
      showToast('success', 'Grade updated successfully');
      resetForm();
      fetchGrades();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to update grade');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (grade) => {
    setEditingGrade(grade);
    setFormData({
      name: grade.name || '',
      is_active: grade.is_active !== false,
    });
    setShowModal(true);
  };

  const handleDelete = async (gradeId, name) => {
    if (!window.confirm(`Delete "${name}"? This will deactivate the grade.`)) return;

    try {
      await publisherGradeAPI.deleteGrade(gradeId);
      showToast('success', 'Grade deleted successfully');
      fetchGrades();
    } catch (error) {
      showToast('error', error.response?.data?.error || 'Failed to delete grade');
    }
  };

  if (loading) {
    return (
      <DashboardLayout menuItems={menuItems}>
        <DashboardContainer>
          <LoadingSpinner size="large" text="Loading grades..." />
        </DashboardContainer>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout menuItems={menuItems}>
      <DashboardContainer>
        <DashboardHeader>
          <div>
            <h1>Grades</h1>
            <p>Create, update, and manage publisher grade levels</p>
          </div>
          <Button leftIcon={<FiPlus />} onClick={() => setShowModal(true)}>
            New Grade
          </Button>
        </DashboardHeader>

        <FilterBar>
          <SearchInput
            placeholder="Search grades..."
            value={filters.search}
            onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
          />
          <FilterSelect
            value={filters.is_active}
            onChange={(e) => setFilters({ ...filters, is_active: e.target.value, page: 1 })}
          >
            <option value="">All Status</option>
            <option value="true">Active</option>
            <option value="false">Inactive</option>
          </FilterSelect>
          <FilterSelect
            value={filters.sort_by}
            onChange={(e) => setFilters({ ...filters, sort_by: e.target.value, page: 1 })}
          >
            <option value="name">Sort by Name</option>
            <option value="created_at">Sort by Created</option>
            <option value="updated_at">Sort by Updated</option>
          </FilterSelect>
          <FilterSelect
            value={filters.sort_order}
            onChange={(e) => setFilters({ ...filters, sort_order: e.target.value, page: 1 })}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </FilterSelect>
        </FilterBar>

        <SectionTitle>Grade Catalog</SectionTitle>

        {grades.length === 0 ? (
          <EmptyState>
            <FiLayers size={48} />
            <h3>No grades found</h3>
            <p>Create your first grade to start organizing publications</p>
            <Button leftIcon={<FiPlus />} onClick={() => setShowModal(true)}>
              Create Grade
            </Button>
          </EmptyState>
        ) : (
          <GradeGrid>
            {grades.map((grade) => (
              <GradeCard key={grade.id}>
                <GradeCardHeader>
                  <div>
                    <h3>{grade.name}</h3>
                    <span className="badge">
                      {grade.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </GradeCardHeader>
                <GradeCardBody>
                  <div className="meta">
                    <span>{grade.is_active ? 'Active' : 'Inactive'}</span>
                    <span>Updated {new Date(grade.updated_at || grade.created_at).toLocaleDateString()}</span>
                  </div>
                </GradeCardBody>
                <GradeCardFooter>
                  <GradeActions>
                    <ActionButton title="Edit" onClick={() => handleEdit(grade)}>
                      <FiEdit />
                    </ActionButton>
                    <ActionButton title="Delete" onClick={() => handleDelete(grade.id, grade.name)}>
                      <FiTrash2 />
                    </ActionButton>
                  </GradeActions>
                </GradeCardFooter>
              </GradeCard>
            ))}
          </GradeGrid>
        )}

        {pagination?.pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginTop: '24px' }}>
            <Button
              variant="ghost"
              disabled={pagination.page === 1}
              onClick={() => setFilters({ ...filters, page: pagination.page - 1 })}
            >
              Previous
            </Button>
            <span style={{ display: 'flex', alignItems: 'center', padding: '0 12px' }}>
              Page {pagination.page} of {pagination.pages}
            </span>
            <Button
              variant="ghost"
              disabled={pagination.page === pagination.pages}
              onClick={() => setFilters({ ...filters, page: pagination.page + 1 })}
            >
              Next
            </Button>
          </div>
        )}

        <Modal
          isOpen={showModal}
          onClose={resetForm}
          title={editingGrade ? 'Edit Grade' : 'Create Grade'}
          size="medium"
        >
          <form onSubmit={editingGrade ? handleUpdate : handleCreate}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <Input
                label="Grade Name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Grade 3"
                required
              />

              {editingGrade && (
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>
                    Status
                  </label>
                  <select
                    value={formData.is_active ? 'true' : 'false'}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.value === 'true' })}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '2px solid #E5E7EB',
                      borderRadius: '12px',
                      fontSize: '16px',
                      backgroundColor: '#F9FAFB',
                    }}
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <Button variant="ghost" type="button" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit" loading={submitting}>
                  {editingGrade ? 'Update Grade' : 'Create Grade'}
                </Button>
              </div>
            </div>
          </form>
        </Modal>
      </DashboardContainer>
    </DashboardLayout>
  );
};

export default PublisherGrades;
